import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { OccEndpointsService, Product, ProductConnector, ProductService, RoutingService, TranslatePipe } from '@spartacus/core';
import { CurrentProductService, OutletDirective, ProductSummaryComponent } from '@spartacus/storefront';
import { Subject, Subscription } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { KnBrCommonService } from 'src/app/services/kn-br-common.service';
import { ProductSwitchService } from 'src/app/services/kn-br-product-switch.service';
import { KnBrCustomerContextService } from 'src/app/store/kn-br-customer-context/kn-br-customer-context.service';
import { KN_BR_CUSTOMER_CURRENT } from 'src/constants/api.endpoints.constant';

@Component({
    selector: 'kn-br-product-summary',
    templateUrl: './kn-br-product-summary.component.html',
    styleUrls: ['./kn-br-product-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [OutletDirective, AsyncPipe, LowerCasePipe, TranslatePipe, FormsModule, ReactiveFormsModule, NgSelectModule]
})
export class KnBrProductSummaryComponent extends ProductSummaryComponent {
  subscription: Subscription;
  private destroy$ = new Subject<void>();
  pdpProductData;
  defaultVariantProductCode = '';
  // product$: Observable<Product | null>;
  variantProductData: any = {};
  variantOptionsDropdownData = [];

  variantForm!: FormGroup;

  constructor(
    protected service: CurrentProductService,
    protected productService: ProductService,
    protected routingService: RoutingService,
    private knBrCommonService: KnBrCommonService,
    private productSwitchService: ProductSwitchService,
    private productConnector: ProductConnector,
    private knBrCustomerContextService: KnBrCustomerContextService,
    private fb: FormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute,
    private endPointService: OccEndpointsService,
    protected cdr: ChangeDetectorRef
  ) {
    super(service);
    this.knBrCommonService.setSpinner(false);
  }

    ngOnInit(): void {
    this.variantForm = this.fb.group({
      variant: [null],
    });

    this.knBrCommonService.setSpinner(true);

    this.route.paramMap.pipe(takeUntil(this.destroy$),
    switchMap((params) => {
      const productCode = params.get('productCode');
      const salesOrg = params.get('salesOrg');

      return this.knBrCustomerContextService.getActiveCustomerNumber().pipe(
        take(1), 
        map((customerNum) => customerNum || KN_BR_CUSTOMER_CURRENT),
        switchMap((customerId) =>
          this.loadBaseProductDetails(productCode, customerId, salesOrg)
        )
      );
    })
  )
  .subscribe((product: any) => {
    if (product) {
      this.pdpProductData = product;
      if (product?.variantOptions?.length) {
        this.variantOptionsDropdownData = product.variantOptions.map((eachVariant: any) => ({
              name: eachVariant.webName ? `${eachVariant.code} - ${eachVariant.webName}` : eachVariant.code,
          value: eachVariant.code,
        }));
      }
      if (product?.defaultVariantProduct) {
            this.variantForm.patchValue({ variant: product?.defaultVariantProduct });
        this.knBrCommonService.setSpinner(true);
        this.defaultVariantProductCode = product.defaultVariantProduct;
        this.getVariantProductData(this.defaultVariantProductCode);
      }
    }
  });
  }

  loadBaseProductDetails(productCode: string, customerId: string, salesOrg: string) {
    const genericCode = this.route.snapshot.queryParamMap.get('genericVariantProduct');

    const url = this.endPointService.buildUrl('product', {
      scope: 'details',
      urlParams: {
        productCode,
        customerId,
        salesOrg,
      },
    });
    // adding custom query param
    const finalUrl = genericCode ? url + (url.includes('?') ? '&' : '?') + `genericVariantProduct=${genericCode}` : url;

    return this.http.get<Product>(finalUrl);
  }

  getVariantProductData(code: string) {
    this.productConnector.get(code, 'specificVariant' ).subscribe((p: any) => {
      this.variantProductData = p;
      this.productSwitchService.updateProductCode(p);
      this.knBrCommonService.setSpinner(false);
      this.cdr.detectChanges();
    });
  }

  handleVariantProductChange(selectedValue: any) {
    this.knBrCommonService.setSpinner(true);
    this.getVariantProductData(selectedValue.value);
  }

  navigateTo(panelId) {
    const techDocId: any = document.querySelectorAll(`[id=${'technicaldocsection'}]`);
    if (techDocId) {
      const isExpanded = techDocId[0].parentElement.getAttribute('aria-expanded');
      if (isExpanded === 'false') {
        techDocId[0].click();
      }
    }
    window.scrollBy(0, 500);
  }

  shouldProductandTechnicalDocumentationDisplay() {
    if (this.pdpProductData) {
      const product = this.pdpProductData;
      if (
        product.assetName ||
        product.assetNameEs ||
        product.assetNameFr ||
        product.productSellSheetName ||
        product.productSellSheetNameEs ||
        product.productSellSheetNameFr ||
        product.safetyAssetName ||
        product.safetyAssetNameEs ||
        product.safetyAssetNameFr
      ) {
        return true;
      }
      return false;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.productSwitchService.updateProductCode(null);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
